import type { components } from "./schema";

export type ErrorEnvelope = components["schemas"]["ErrorEnvelope"];

/**
 * Trate sempre pelo `code`. O `message` é texto pra humano em pt-BR e a API
 * avisa que pode mudar sem aviso — nunca faça lógica em cima dele.
 */
export function errorCode(error: unknown): string | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as ErrorEnvelope).error?.code === "string"
  ) {
    return (error as ErrorEnvelope).error.code;
  }
  return null;
}

/**
 * Erro de API que carrega o `code` junto. Sem isto, quem pega a exceção lá
 * na tela só tem o `message` — e fazer `switch` em cima de texto pra humano é
 * exatamente o que o comentário do `errorCode` manda não fazer.
 */
export class ApiError extends Error {
  readonly code: string;
  /**
   * Identificador de rastreio do terceiro que falhou — hoje o `fbtrace_id`
   * da Meta, que o suporte deles pede em qualquer chamado. Só vem quando a
   * falha foi de fora; erro nosso não tem.
   */
  readonly traceId: string | null;
  constructor(code: string, message: string, traceId: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.traceId = traceId;
  }

  /**
   * Falha do lado de lá, não do dono: tentar de novo tem chance real de
   * funcionar, e a tela deve oferecer o botão em vez de só mostrar texto
   * vermelho. `meta_unavailable` chega com 502.
   */
  get retriable(): boolean {
    return this.code === "meta_unavailable" || this.code === "meta_rate_limited";
  }
}

/** Lê o `trace_id` do envelope de erro, quando houver. */
export function errorTraceId(error: unknown): string | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as ErrorEnvelope).error?.trace_id === "string"
  ) {
    return (error as ErrorEnvelope).error.trace_id as string;
  }
  return null;
}

export function errorMessage(error: unknown, fallback = "Algo deu errado."): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as ErrorEnvelope).error?.message === "string"
  ) {
    return (error as ErrorEnvelope).error.message;
  }
  return fallback;
}
